import { concat } from '@ember/helper';
import { service } from '@ember/service';
import type { ValidatorMap } from 'ember-changeset/types';
import t from 'ember-intl/helpers/t';
import type IntlService from 'ember-intl/services/intl';
import CrudJournalEventBaseComponent from 'parog-web/components/forc/abstract/crud-journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import JournalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import DiabetowiseDischargeCompletedJournalEvent from 'parog-web/models/diabetowise-discharge-completed-journal-event';

export default class DiabetowiseDischargeEditionFormComponent extends CrudJournalEventBaseComponent<DiabetowiseDischargeCompletedJournalEvent> {
  static forcDisplayedName = 'Fiche de sortie';
  static override fullComponentId = 'diabetowise-discharge/edition-form';
  static override journalEventClass = DiabetowiseDischargeCompletedJournalEvent;

  @service declare intl: IntlService;

  override journalEventProperties = [
    'lastName',
    'firstName',
    'age',
    'gender',
    'prognosticLifeThreatening',
    'smoker',
    'weight',
    'height',
    'doSport',
    'phone',
    'met',
    'isglt2',
    'arglp1',
    'insulin',
    'idpp4',
    'hba1c',
    'hypoglycaemia',
    'severeHypoglycaemia',
    'hyperglycaemia',
    'severeHyperglycaemia',
    'hospitalisation',
    'neuropathie',
    'chronicRenalFailure',
    'dfg',
    'albuminuria',
    'rac',
    'mcva',
    'heartFailure',
    'fevg',
    'severeComorbidity',
    'hta',
    'systolic',
    'diastolic',
    'dyslipidemia',
    'comments',
    'occurredAt'
  ];
  override retrieveJournalEvent =
    JournalEventRetrievalStrategies.sourceJournalEvent;

  override get initialModel() {
    return {
      lastName: this.patient.lastName,
      firstName: this.patient.firstName,
      age: this.patient.age,
      gender: this.patient.gender,
      height:
        this.patient.lastHeightMeasuredJournalEvent?.height ||
        this.patient.medicalHistoryChangedJournalEvent?.height,
      phone: this.patient.phone,
      met: this.patient.medicalHistoryChangedJournalEvent?.met,
      isglt2: this.patient.medicalHistoryChangedJournalEvent?.isglt2,
      arglp1: this.patient.medicalHistoryChangedJournalEvent?.arglp1,
      insulin: this.patient.medicalHistoryChangedJournalEvent?.insulin,
      idpp4: this.patient.medicalHistoryChangedJournalEvent?.idpp4
    };
  }

  override get validations(): ValidatorMap {
    return this.journalEventClass.validations(this.intl);
  }

  <template>
    <Provider @forcComponent={{this}} @title='Fiche de sortie'>
      <:content as |Form|>

        <div class='title'>
          <h3 class='ui dividing header mb16'>
            Patient suivi pour
          </h3>
        </div>

        {{t
          (concat
            'therapeutical-area.'
            this.patient.followUpReasonRecord.therapeuticalArea.id
          )
        }}
        :
        {{t (concat 'follow-up-reason.' this.patient.followUpReasonRecord.id)}}

        <div class='title'>
          <h3 class='ui dividing header mb16'>
            Données du patient
          </h3>
        </div>

        <div class='two fields'>
          <div class='field'>
            <Form.Input
              @propertyName='lastName'
              @labelName='Nom utilisé'
              @isMandatory={{true}}
            />
          </div>
          <div class='field'>
            <Form.Input
              @propertyName='firstName'
              @labelName='Prénom utilisé'
              @isMandatory={{true}}
            />
          </div>
        </div>

        <div class='two fields'>
          <div class='field'>
            <Form.NumberInput
              @propertyName='age'
              @labelName='Âge'
              @isMandatory={{true}}
            />
          </div>
          <div class='field'>
            <Form.Dropdown
              @labelName='Sexe'
              @propertyName='gender'
              @isMandatory={{true}}
              as |dropdown|
            >
              <dropdown.Item @key='male' @value='Homme' />
              <dropdown.Item @key='female' @value='Femme' />
            </Form.Dropdown>
          </div>
        </div>

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title='Pathologie évolutive ou menaçant le pronostic vital à court terme'
            @propertyName='prognosticLifeThreatening'
            @isMandatory={{true}}
            as |Group|
          >
            <Group.FieldRadio @value={{true}} @label='Oui' />
            <Group.FieldRadio @value={{false}} @label='Non' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='two fields'>
          <div class='field'>
            <Form.NumberInput
              @propertyName='weight'
              @labelName='Poids (kg)'
              @isMandatory={{true}}
            />
          </div>
          <div class='field'>
            <Form.NumberInput
              @propertyName='height'
              @labelName='Taille (cm)'
              @isMandatory={{true}}
            />
          </div>
        </div>

        <div class='two fields'>
          <div class='field'>
            <Form.AlignedFieldsRadioGroup
              @title='Fumeur'
              @propertyName='smoker'
              @isMandatory={{true}}
              as |Group|
            >
              <Group.FieldRadio @value={{true}} @label='Oui' />
              <Group.FieldRadio @value={{false}} @label='Non' />
            </Form.AlignedFieldsRadioGroup>
          </div>
          <div class='field'>
            <Form.AlignedFieldsRadioGroup
              @title='Activité physique'
              @propertyName='doSport'
              @isMandatory={{true}}
              as |Group|
            >
              <Group.FieldRadio @value='yes' @label='Oui' />
              <Group.FieldRadio @value='no' @label='Non' />
              <Group.FieldRadio @value='stopped' @label='A arrêté' />
            </Form.AlignedFieldsRadioGroup>
          </div>
        </div>

        <div class='two fields'>
          <div class='field'>
            <Form.Phone
              @propertyName='phone'
              @labelName='Numéro de téléphone'
              @isMandatory={{true}}
            />
          </div>
        </div>

        <div class='title'>
          <h3 class='ui dividing header mb16'>
            Données du dossier médical
          </h3>
        </div>

        <div class='two fields'>
          <div class='field'>
            <Form.NumberInput
              @propertyName='hba1c'
              @labelName='HbA1c (%) à 1 an'
              @isMandatory={{true}}
            />
          </div>
        </div>

        <div class='ui dividing header mb16' />

        <div class='title'>
          <h4 class='ui header mb16'>Complications apparues à 1 an</h4>
        </div>
        <div class='title'>
          <h4 class='ui header mb16'>Accident métabolique</h4>
        </div>

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title='Hypoglycémie au cours de l’année écoulée :'
            @displaySubText={{true}}
            @subText='Hypoglycémie si la glycémie est < 0,70 g/L.'
            @propertyName='hypoglycaemia'
            @isMandatory={{true}}
            as |Group|
          >
            <Group.FieldRadio @value='yes' @label='Oui' />
            <Group.FieldRadio @value='no' @label='Non' />
            <Group.FieldRadio @value='unknown' @label='Je ne sais pas' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title='Si oui, sévère :'
            @displaySubText={{true}}
            @subText='Si elle nécessite l’aide d’une tierce personne pour le resucrage.'
            @propertyName='severeHypoglycaemia'
            as |Group|
          >
            <Group.FieldRadio @value='yes' @label='Oui' />
            <Group.FieldRadio @value='no' @label='Non' />
            <Group.FieldRadio @value='unknown' @label='Je ne sais pas' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title='Hyperglycémie au cours de l’année écoulée :'
            @propertyName='hyperglycaemia'
            @isMandatory={{true}}
            as |Group|
          >
            <Group.FieldRadio @value='yes' @label='Oui' />
            <Group.FieldRadio @value='no' @label='Non' />
            <Group.FieldRadio @value='unknown' @label='Je ne sais pas' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title='Si oui, sévère :'
            @displaySubText={{true}}
            @subText='Hyperglycémie si la glycémie est > 2,50 g/L à jeun ou non.'
            @propertyName='severeHyperglycaemia'
            as |Group|
          >
            <Group.FieldRadio @value='yes' @label='Oui' />
            <Group.FieldRadio @value='no' @label='Non' />
            <Group.FieldRadio @value='unknown' @label='Je ne sais pas' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='ui dividing header mb16' />

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title='Hospitalisation pour insuffisance cardiaque, insuffisance rénale aiguë/dialyse, maladie cardiovasculaire athéromateuse, au cours de l’année écoulée :'
            @isMandatory={{true}}
            @propertyName='hospitalisation'
            as |Group|
          >
            <Group.FieldRadio @value='yes' @label='Oui' />
            <Group.FieldRadio @value='no' @label='Non' />
            <Group.FieldRadio @value='unknown' @label='Je ne sais pas' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='ui dividing header mb16' />

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title='Neuropathie au cours de l’année écoulée :'
            @isMandatory={{true}}
            @propertyName='neuropathie'
            as |Group|
          >
            <Group.FieldRadio @value='yes' @label='Oui' />
            <Group.FieldRadio @value='no' @label='Non' />
            <Group.FieldRadio @value='unknown' @label='Je ne sais pas' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='ui dividing header mb16' />

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title='Insuffisance rénale chronique au cours de l’année écoulée :'
            @isMandatory={{true}}
            @propertyName='chronicRenalFailure'
            as |Group|
          >
            <Group.FieldRadio @value='yes' @label='Oui' />
            <Group.FieldRadio @value='no' @label='Non' />
            <Group.FieldRadio @value='unknown' @label='Je ne sais pas' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='two fields'>
          <div class='field'>
            <Form.NumberInput
              @propertyName='dfg'
              @labelName='DFG formule CKD-EPI (en mL/min/1,73 m2) :'
              @placeholder='Ex : 30'
            />
          </div>
        </div>

        <div class='ui dividing header mb16' />

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title='Albuminurie pathologique au cours de l’année écoulée :'
            @displaySubText={{true}}
            @subText='Micro-albuminurie RAC > 30 mg/g, macro-albuminurie RAC > 300 mg/g'
            @isMandatory={{true}}
            @propertyName='albuminuria'
            as |Group|
          >
            <Group.FieldRadio @value='yes' @label='Oui' />
            <Group.FieldRadio @value='no' @label='Non' />
            <Group.FieldRadio @value='unknown' @label='Je ne sais pas' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='two fields'>
          <div class='field'>
            <Form.NumberInput
              @propertyName='rac'
              @labelName='RAC (en mg/g) :'
              @placeholder='Ex : 150'
            />
          </div>
        </div>

        <div class='ui dividing header mb16' />

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title='Maladie cardiovasculaire athéromateuse (MCVA) au cours de l’année écoulée :'
            @displaySubText={{true}}
            @subText='Maladie des coronaires (IDM, Angor avec preuve coronaire, sténose de coronaire, carotide ou a. périphérique > 50%), maladie cérébrovaculaires (AVC ischémique, AIT, sténose carotidienne), AOMI (ATCD de revascularisation, amputation ou claudication avec preuve d’imagerie d’origine vasculaire).'
            @isMandatory={{true}}
            @propertyName='mcva'
            as |Group|
          >
            <Group.FieldRadio @value='yes' @label='Oui' />
            <Group.FieldRadio @value='no' @label='Non' />
            <Group.FieldRadio @value='unknown' @label='Je ne sais pas' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='ui dividing header mb16' />

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title='Insuffisance cardiaque au cours de l’année écoulée :'
            @isMandatory={{true}}
            @propertyName='heartFailure'
            as |Group|
          >
            <Group.FieldRadio @value='yes' @label='Oui' />
            <Group.FieldRadio @value='no' @label='Non' />
            <Group.FieldRadio @value='unknown' @label='Je ne sais pas' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='two fields'>
          <div class='field'>
            <Form.NumberInput
              @propertyName='fevg'
              @labelName='Si oui, FEVG (%) :'
              @placeholder='Ex: 40'
            />
          </div>
        </div>

        <div class='ui dividing header mb16' />

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title='Autres comorbidités sévères au cours de l’année écoulée :'
            @isMandatory={{true}}
            @propertyName='severeComorbidity'
            as |Group|
          >
            <Group.FieldRadio @value='yes' @label='Oui' />
            <Group.FieldRadio @value='no' @label='Non' />
            <Group.FieldRadio @value='unknown' @label='Je ne sais pas' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='ui dividing header mb16' />

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title='HTA au cours de l’année écoulée :'
            @isMandatory={{true}}
            @propertyName='hta'
            as |Group|
          >
            <Group.FieldRadio @value='treated' @label='Oui, traitée' />
            <Group.FieldRadio @value='yes' @label='Oui, non traitée' />
            <Group.FieldRadio @value='no' @label='Non' />
            <Group.FieldRadio @value='unknown' @label='Je ne sais pas' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='two fields'>
          <div class='field'>
            <Form.NumberInput
              @propertyName='systolic'
              @labelName='Dernière pression artérielle systolique en consultation :'
              @placeholder='Ex: 120'
            />
          </div>
        </div>

        <div class='two fields'>
          <div class='field'>
            <Form.NumberInput
              @propertyName='diastolic'
              @labelName='Dernière pression artérielle diastolique en consultation :'
              @placeholder='Ex: 60'
            />
          </div>
        </div>

        <div class='ui dividing header mb16' />

        <div class='title'>
          <h4 class='ui header mb16'>Dyslipidémie au cours de l’année écoulée :</h4>
        </div>

        <div class='two fields'>
          <div class='field'>
            <Form.NumberInput
              @propertyName='dyslipidemia'
              @labelName='LDLc (en g/L) :'
              @placeholder='Ex: 1,2'
              @isMandatory={{true}}
            />
          </div>
        </div>

        <div class='title'>
          <h3 class='ui dividing header mb16'>
            Traitements en cours
          </h3>
        </div>

        <div class='field'>
          <label>Metformine *</label>
          <div class='fields inline'>
            <Form.BasicRadioGroup @propertyName='met' as |Group|>
              <Group.FieldRadio @label='Oui' @value={{true}} />
              <Group.FieldRadio @label='Non' @value={{false}} />
            </Form.BasicRadioGroup>
          </div>
        </div>
        <div class='field'>
          <label>iSGLT2 *</label>
          <div class='fields inline'>
            <Form.BasicRadioGroup @propertyName='isglt2' as |Group|>
              <Group.FieldRadio @label='Oui' @value={{true}} />
              <Group.FieldRadio @label='Non' @value={{false}} />
            </Form.BasicRadioGroup>
          </div>
        </div>
        <div class='field'>
          <label>AR GLP-1 *</label>
          <div class='fields inline'>
            <Form.BasicRadioGroup @propertyName='arglp1' as |Group|>
              <Group.FieldRadio @label='Oui' @value={{true}} />
              <Group.FieldRadio @label='Non' @value={{false}} />
            </Form.BasicRadioGroup>
          </div>
        </div>
        <div class='field'>
          <label>Insuline *</label>
          <div class='fields inline'>
            <Form.BasicRadioGroup @propertyName='insulin' as |Group|>
              <Group.FieldRadio @label='Oui' @value={{true}} />
              <Group.FieldRadio @label='Non' @value={{false}} />
            </Form.BasicRadioGroup>
          </div>
        </div>

        <div class='field'>
          <label>iDPP4 *</label>
          <div class='fields inline'>
            <Form.BasicRadioGroup @propertyName='idpp4' as |Group|>
              <Group.FieldRadio @label='Oui' @value={{true}} />
              <Group.FieldRadio @label='Non' @value={{false}} />
            </Form.BasicRadioGroup>
          </div>
        </div>

        <div class='ui dividing header mb16' />

        <p>
          <span class='font-bold'>Attention</span>, en cas de réponse du type
          “je ne sais pas” à certains items, une infirmière Sêmeia sera
          susceptible de vous recontacter.
        </p>

      </:content>
    </Provider>
  </template>
}
